import { useState, useCallback } from 'react';
import useCopilotChatContext from './useCopilotChatContext';

/**
 * Custom hook for handling "Start new chat" functionality
 * Centralizes the logic for creating a new conversation
 *
 * When in campaign mode, this switches to the Fusion Copilot tab.
 * When in Fusion mode, this creates a new Fusion Copilot tab (Fusion Copilot 2, 3, etc.)
 */
export const useNewChat = () => {
	const {
		assistantAlias,
		setIsLoading,
		isCampaignMode,
		exitCampaignMode,
		createFusionConversation,
		isCreatingFusionConversation,
		fusionConversations,
	} = useCopilotChatContext();
	const [isResetting, setIsResetting] = useState(false);

	const handleNewChat = useCallback(async (): Promise<boolean> => {
		// If in campaign mode, switch to Fusion Copilot tab instead of creating new
		if (isCampaignMode) {
			console.log('[useNewChat] In campaign mode - switching to Fusion Copilot tab');
			exitCampaignMode();
			return true;
		}

		if (isResetting || isCreatingFusionConversation || !assistantAlias) {
			if (!assistantAlias) {
				console.error('assistantAlias not available');
			}
			return false;
		}

		try {
			// Show loading indicator immediately
			setIsLoading(true);
			setIsResetting(true);
			console.log('[useNewChat] Creating new Fusion conversation tab');

			// Create a new Fusion conversation tab
			const success = await createFusionConversation();

			console.log('[useNewChat] New Fusion conversation created, success:', success);
			return success;
		} catch (error) {
			console.error('[useNewChat] Failed to create new Fusion conversation:', error);
			return false;
		} finally {
			setIsResetting(false);
			setIsLoading(false);
		}
	}, [isResetting, isCreatingFusionConversation, assistantAlias, setIsLoading, isCampaignMode, exitCampaignMode, createFusionConversation]);

	// Hide the button when there are no Fusion conversations (empty state shows its own button)
	const hasFusionConversations = fusionConversations.length > 0;

	return {
		handleNewChat,
		isResetting: isResetting || isCreatingFusionConversation,
		canStartNewChat: !!assistantAlias && hasFusionConversations,
	};
};
